import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  signal,
  input,
  HostBinding,
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import * as moment from 'moment';
import {Project} from 'src/app/core/model/project';
import {TimezoneService} from 'src/app/services/timezone.service';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';

@Component({
  selector: 'project-selector',
  standalone: true,
  imports: [MatMenuModule, MatIconModule, MatProgressSpinnerModule, PipesModule, CommonModule],
  templateUrl: './project-selector.component.html',
  styleUrl: './project-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectSelectorComponent implements OnChanges {
  @Input() selectedProject: Project;
  @Input() loading: boolean;
  @Input() hideActionButton: boolean;
  @Input() buttonStyle: Object;
  @Input() isProjectActive: boolean;
  @Input() loadingProject: boolean;
  hostStyle = input<Record<string, string>>();

  hostStyleString = computed(() => {
    const style = this.hostStyle();
    if (!style) return null;
    return Object.entries(style)
      .map(([key, value]) => {
        // Convert camelCase to kebab-case for CSS properties
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        return `${cssKey}: ${value}`;
      })
      .join('; ');
  });

  @HostBinding('attr.style')
  get computedHostStyle(): string | null {
    return this.hostStyleString();
  }

  private buttonState = signal<{
    display: string;
    textColor: string;
    backgroundColor: string;
  }>({
    display: 'Draft',
    textColor: '#A3A099',
    backgroundColor: '#E8E6E1',
  });

  buttonDisplay = computed(() => this.buttonState().display);
  statusPillStyle = computed(() => {
    const state = this.buttonState();
    return {
      color: state.textColor || '#A3A099',
      'background-color': state.backgroundColor || '#E8E6E1',
    };
  });

  constructor(private timezoneService: TimezoneService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedProject && this.selectedProject) {
      this.setButtonDisplay();
    }
  }

  setButtonDisplay() {
    const now = moment().tz(this.timezoneService.getTimeZone()).startOf('day');
    const projectStart = moment(this.selectedProject.startDateTime).tz(this.timezoneService.getTimeZone());

    if (
      this.selectedProject.status === 'inactive' ||
      !this.selectedProject.startDateTime ||
      !this.selectedProject.endDateTime
    ) {
      this.buttonState.set({
        display: 'Draft',
        textColor: '#A3A099',
        backgroundColor: '#E8E6E1',
      });
    } else if (this.selectedProject.status === 'active' && now.isBefore(projectStart)) {
      this.buttonState.set({
        display: 'Scheduled',
        textColor: '#E65100',
        backgroundColor: '#FFE0B2',
      });
    } else if (
      this.selectedProject.status === 'active' &&
      this.timezoneService.areDatesWithinRange(this.selectedProject.startDateTime, this.selectedProject.endDateTime)
    ) {
      this.buttonState.set({
        display: 'Active',
        textColor: '#599034',
        backgroundColor: '#BFFBBD',
      });
    } else if (
      this.selectedProject.status === 'active' &&
      !this.timezoneService.areDatesWithinRange(this.selectedProject.startDateTime, this.selectedProject.endDateTime)
    ) {
      this.buttonState.set({
        display: 'Expired',
        textColor: '#B71C1C',
        backgroundColor: '#FFCDD2',
      });
    }
  }
}
